import Avo from '@susu/custom-made/Avo';
import CardList, { CardListType } from '@susu/custom-made/components/cardList/cardList';
import useScrollUp from '@susu/custom-made/hooks/useScrollUp';
import { ThemeContext } from '@susu/custom-made/providers';
import { actions } from '@susu/custom-made/providers/reducers/fabric';
import {
  allFabricsGroupId,
  Clients,
  ConfiguratorOrigin,
  ConfiguratorOriginsType,
  Level,
  sortList,
} from '@susu/custom-made/utils/constants';
import type {
  Fabric,
  FabricFilter,
  FabricGroup,
  typeFabricList,
  typefilterAttributeName,
} from '@susu/custom-made/utils/interfaces';
import { changeLevel } from '@susu/custom-made/utils/navigation';
import {
  calculateFabricPrice,
  filterLowStockFabricList,
  getAvoEventParams,
  getAvoEventParamsWithOption,
  onEnter,
} from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import Button, { ButtonSize, ButtonType } from '../button/button';
import type { typeCard } from '../card/card';
import Panel, { PanelSize } from '../panel/panel';
import styles from './fabricPanel.module.scss';
import { OnBoardingTips } from '../onBoarding/onBoardingTips/onBoardingTips';
const FilterList = dynamic(() => import('../filterList/filterList'), {ssr: false});
const SearchList = dynamic(() => import('../searchList/searchList'), {ssr: false});
const FabricInfo = dynamic(() => import('../fabricInfo/fabricInfo'), {ssr: false});

export const FabricPanel = ({
  fabricGroups,
  fabricList,
  fabricFilters,
  fabricPreFilters,
  navigation,
  isCustomizeFlow,
  origin,
  isFirstRender,
  applyFabricBusinessRules,
}: {
  fabricGroups: FabricGroup[];
  fabricList: typeFabricList;
  fabricFilters: FabricFilter[];
  fabricPreFilters: Record<string, string[]> | null;
  navigation: React.ReactNode;
  isCustomizeFlow: boolean;
  origin: ConfiguratorOriginsType;
  isFirstRender: boolean;
  applyFabricBusinessRules: (selectedFabric: Fabric, prevFabric?: Fabric) => void;
}) => {
  const defaultFabricKeys = filterLowStockFabricList(Object.keys(fabricList), fabricList);
  const { dispatch, state } = useContext(ThemeContext);
  const [isSearchShown, setIsSearchShown] = useState<boolean>(false);
  const [isFilterShown, setIsFilterShown] = useState<boolean>(false);
  const [isFabricInfoShown, setIsFabricInfoShown] = useState<boolean>(false);
  const [subGroupFabricListIds, setSubGroupFabricListIds] = useState<string[]>([]);
  const [selectedSortBy, setSelectedSortBy] = useState<string>(sortList.Default);
  const [filteredFabricListIds, setFilteredFabricListIds] = useState<string[]>(defaultFabricKeys);

  const [selectedFilter, setSelectedFilter] = useState<Record<string, string[]>>({});
  const [clickedFabricGroup, setClickedFabricGroup] = useState<FabricGroup>();
  const [scrollStatus, setScrollStatus] = useState<boolean>(false);
  const cardlistRef = useRef<HTMLInputElement>(null);
  const scrollUp = useScrollUp();
  const [scanTrigger, setScanTrigger] = useState<boolean>(false);
  const [isSearchShortcut, setIsSearchShortcut] = useState<boolean>(false);

  const onContentScroll = (e: React.UIEvent<HTMLElement>) => {
    if (e.currentTarget.scrollTop > 1) {
      setScrollStatus(true);
    } else {
      setScrollStatus(false);
    }
  };

  const navigateToFabricGroup = useCallback(
    (fabricGroup: FabricGroup) => {
      changeLevel(Level.Subgroup, dispatch);
      const tmpFabric = filterLowStockFabricList(fabricGroup.fabrics.map(String), fabricList);
      setFilteredFabricListIds(tmpFabric);
      setSubGroupFabricListIds(tmpFabric);
      setClickedFabricGroup(fabricGroup);
    },
    [dispatch, fabricList],
  );

  const openFabricPanel = () => {
    setIsFilterShown(true);
    state.client === Clients.onLine && Avo.fabricFilter(getAvoEventParamsWithOption(state, `${state.selectedFabricGroup?.name}`));
  };

  const openSearchPanel = () => {
    setIsSearchShown(true);
    state.client === Clients.onLine && Avo.searchClicked(getAvoEventParams(state));
  };

  const scanClick = () => {
    openSearchPanel();
    setScanTrigger(true);
  };

  const searchClick = () => {
    const allFabrics = fabricGroups.find(({ id }) => id === allFabricsGroupId);
    if (allFabrics && Object.keys(allFabrics.fabrics).length > 0) {
      setIsSearchShortcut(true);
      navigateToFabricGroup(allFabrics);
      openSearchPanel();
    }
  };

  const getFilteredFabrics = useCallback(
    (selectedFabricIds: string[], selectedFilters: Record<string, string[]>) => {
      let tmpFilteredFabrics = [...selectedFabricIds];
      if (Object.keys(selectedFilters).length > 0) {
        for (const filterName in selectedFilters) {
          if (selectedFilters[filterName].length > 0) {
            tmpFilteredFabrics = tmpFilteredFabrics.filter(
              (id: string) =>
                selectedFilters[filterName].indexOf(fabricList[id][filterName as typefilterAttributeName]) >= 0,
            );
          }
        }
      }
      return tmpFilteredFabrics;
    },
    [fabricList],
  );

  const getSortedFabrics = useCallback(
    (selectedFabricIds: string[], selectedSortBy: string) => {
      let tmpFilteredFabrics = [...selectedFabricIds];
      if (selectedSortBy === sortList.Newest) {
        tmpFilteredFabrics.sort((a: string, b: string) => {
          return fabricList[b].id - fabricList[a].id;
        });
      } else if (selectedSortBy === sortList.PriceHighLow) {
        tmpFilteredFabrics.sort((a: string, b: string) => {
          return (
            calculateFabricPrice(state.product!, fabricList[b].prices.configurationTypes) -
            calculateFabricPrice(state.product!, fabricList[a].prices.configurationTypes)
          );
        });
      } else if (selectedSortBy === sortList.PriceLowHigh) {
        tmpFilteredFabrics.sort((a: string, b: string) => {
          return (
            calculateFabricPrice(state.product!, fabricList[a].prices.configurationTypes) -
            calculateFabricPrice(state.product!, fabricList[b].prices.configurationTypes)
          );
        });
      }
      return tmpFilteredFabrics;
    },
    [fabricList, state.product],
  );

  // When prefilters applies from the url
  useEffect(() => {
    if (fabricPreFilters && isFirstRender) {
      let fabricToSelect = fabricList[fabricGroups[0]?.fabrics[0]];
      let fabricGroupToSelect = fabricGroups[0];
      const allFabrics = fabricGroups.find(({ id }) => id === allFabricsGroupId);
      if (allFabrics && Object.keys(allFabrics.fabrics).length > 0) {
        const allFabricListIds = filterLowStockFabricList(allFabrics?.fabrics.map(String), fabricList);
        const filteredFabrics = getFilteredFabrics(allFabricListIds, fabricPreFilters);
        if (filteredFabrics.length > 0) {
          navigateToFabricGroup(allFabrics);
          setSelectedFilter(fabricPreFilters);
          setFilteredFabricListIds(filteredFabrics);
          fabricToSelect = fabricList[filteredFabrics[0]];
          fabricGroupToSelect = allFabrics;
        }
      }
      if (fabricToSelect) {
        applyFabricBusinessRules(fabricToSelect);
        dispatch({
          type: actions.SELECT_FABRIC,
          payload: fabricToSelect,
        });
        dispatch({
          type: actions.SELECT_FABRIC_GROUP,
          payload: fabricGroupToSelect,
        });
      }
    }
  }, [
    state.selectedFabric,
    fabricList,
    fabricPreFilters,
    fabricGroups,
    isFirstRender,
    dispatch,
    navigateToFabricGroup,
    getFilteredFabrics,
    applyFabricBusinessRules,
  ]);

  const cardClick = useCallback((card: typeCard) => {
    if (state.level === Level.Group) {
      navigateToFabricGroup(card as FabricGroup);
      state.client === Clients.onLine && Avo.fabricGroupSelect(getAvoEventParamsWithOption(state, `${card.name}`));
    } else {
      applyFabricBusinessRules(card as Fabric, state.selectedFabric!);

      dispatch({
        type: actions.SELECT_FABRIC,
        payload: card as Fabric,
      });
      dispatch({
        type: actions.SELECT_FABRIC_GROUP,
        payload: clickedFabricGroup ?? fabricGroups[0],
      });

      state.client === Clients.onLine &&
        Avo.fabricOptionSelect(getAvoEventParamsWithOption(state, `${state.selectedFabricGroup?.name}_${card.name}`));
    }
  }, [
    state.level,
    state.client,
    clickedFabricGroup,
    fabricGroups,
    state.selectedFabric,
    state.selectedFabricGroup,
    applyFabricBusinessRules,
    dispatch,
    navigateToFabricGroup]);

  const searchCardClick = useCallback((card: typeCard) => {
    applyFabricBusinessRules(card as Fabric, state.selectedFabric!);

    dispatch({
      type: actions.SELECT_FABRIC,
      payload: card as Fabric,
    });
  }, [state.selectedFabric, applyFabricBusinessRules, dispatch]);

  const confirmClick = () => {
    state.client === Clients.onLine &&
      Avo.fabricOptionConfirm(
        getAvoEventParamsWithOption(state, `${state.selectedFabricGroup?.name}_${state.selectedFabric?.name}`),
      );
    changeLevel(Level.Group, dispatch);
    setTimeout(() => {
      setFilteredFabricListIds(defaultFabricKeys);
      setSubGroupFabricListIds(defaultFabricKeys);
      setSelectedFilter({});
      setSelectedSortBy(sortList.Default);
      cardlistRef.current?.scrollTo(0, 0);
    }, 400);
  };

  const countFilter = () => {
    const filteredFabricIds = getFilteredFabrics(subGroupFabricListIds, selectedFilter);
    return filteredFabricIds.length;
  };

  const applyFilter = () => {
    const filteredFabricIds = getFilteredFabrics(subGroupFabricListIds, selectedFilter);
    const sortedFabricsIds = getSortedFabrics(filteredFabricIds, selectedSortBy);
    setFilteredFabricListIds(sortedFabricsIds);
  };

  const calculateSelectedFilter = () => {
    return Object.keys(selectedFilter).reduce(
      (sum, key) => sum + parseFloat(selectedFilter[key].length.toString() || '0'),
      0,
    );
  };
  const cardInfoClick = useCallback(() => {
    state.client === Clients.onLine &&
      Avo.fabricInformation(
        getAvoEventParamsWithOption(state, `${state.selectedFabricGroup?.name}_${state.selectedFabric?.name}`),
      );
    setIsFabricInfoShown(true);
  }, [
    state.selectedFabricGroup?.name,
    state.selectedFabric?.name,
    state.client,
    setIsFabricInfoShown]);

  const setTriggerScan = (status: boolean) => {
    setScanTrigger(status);
  };

  const closeSearchPanel = () => {
    setIsSearchShown(false);
    if (isSearchShortcut) {
      confirmClick();
    }
  };

  useEffect(() => {
    if (state.level === Level.Group) {
      setIsSearchShown(false);
      setIsFabricInfoShown(false);
      setIsFilterShown(false);
    }
  }, [state.level]);

  return (
    <>
      {!isCustomizeFlow &&
        state.product === 'Suit' &&
        state.client === 'onLine' &&
        state.level === Level.Subgroup &&
        origin === ConfiguratorOrigin.Normal && <OnBoardingTips />}
      {/* Group section */}
      <div
        className={cn(styles.sectionMain, {
          [styles.isActive]: state.level === Level.Group,
        })}
      >
        {navigation}
        <div
          className={cn(styles.cardListContainer, {
            [styles.scrolled]: scrollStatus,
          })}
          onScroll={onContentScroll}
        >
          <CardList
            cardItems={fabricGroups}
            selectedItemId={state.selectedFabricGroup ? state.selectedFabricGroup.id : null}
            cardClick={cardClick}
            isGroup={true}
            cardListType={CardListType.Fabric}
            testId={'fabric-group-list'}
            section={state.section}
            scanClick={scanClick}
            searchClick={searchClick}
          />
        </div>
      </div>
      {/* Group section */}

      {/* SubGroup section */}
      <div
        className={cn(styles.sectionNext, {
          [styles.isActive]: state.level === Level.Subgroup,
        })}
      >
        <div className={styles.header} data-test-id="fabric-panel-header">
          {clickedFabricGroup?.name ?? state.selectedFabricGroup?.name}
        </div>
        <div
          className={cn(styles.containerTop, {
            [styles.show]: scrollUp.scrollingUp,
          })}
        >
          <div
            className={styles.searchContainer}
            data-test-id="fabric-panel-search-btn"
            onClick={openSearchPanel}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              onEnter(e, openSearchPanel);
            }}
          >
            <i className="susu-icons" translate="no">
              search
            </i>
            <span className={styles.text}>Search</span>
          </div>
          <div
            className={cn(styles.filterContainer, { [styles.selected]: calculateSelectedFilter() > 0 })}
            data-test-id="fabric-panel-filter-btn"
            onClick={openFabricPanel}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              onEnter(e, openFabricPanel);
            }}
          >
            {calculateSelectedFilter() > 0 && <span className={styles.filterAmount}>{calculateSelectedFilter()}</span>}
            <i className="susu-icons" translate="no">
              filter
            </i>
            <span className={styles.text}>Filter</span>
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <Button
            isFullSize={true}
            onClick={confirmClick}
            testId="fabric-panel-confirm-btn"
            type={ButtonType.IconOnlyRound}
            size={ButtonSize.Small}
          >
            <div className={styles.buttonContent}>
              <div className={styles.buttonIcon}>
                <i className="susu-icons" translate="no">
                  arrow_left
                </i>
              </div>
            </div>
          </Button>
        </div>
        <div
          className={cn(styles.cardListContainer, {
            [styles.isSubList]: state.level === Level.Subgroup,
          })}
          ref={cardlistRef}
          onScroll={e => {
            scrollUp.handleScroll(e, 43);
          }}
        >
          <CardList
            cardItems={fabricList}
            cardSubItems={filteredFabricListIds}
            selectedItemId={state.selectedFabric ? state.selectedFabric.id : null}
            cardClick={cardClick}
            infoIconClick={cardInfoClick}
            isGroup={false}
            cardListType={CardListType.Fabric}
            testId="fabric-subgroup-list"
            section={state.section}
          />
        </div>
        {state.level === Level.Subgroup && (
          <div className={styles.cardDetailsContainer}>
            <div className={styles.cardText} data-test-id="card-text-mobile">
              {state.selectedFabric?.name}
            </div>
            <div className={styles.cardSubText} data-test-id="card-subtext-mobile">
              {state.selectedFabric &&
                state.product &&
                state.selectedFabric.prices?.symbol +
                  calculateFabricPrice(state.product, state.selectedFabric.prices?.configurationTypes)}
            </div>
          </div>
        )}
        {/* Subgroup section */}
      </div>

      {/* Search Panel */}
      <Panel isActive={isSearchShown} closeClick={closeSearchPanel} panelSize={PanelSize.Multi}>
        {isSearchShown && (
          <SearchList
            onClose={closeSearchPanel}
            itemList={fabricList}
            filteredItemListIds={filteredFabricListIds}
            selectedItemId={state.selectedFabric ? state.selectedFabric.id : null}
            infoIconClick={cardInfoClick}
            cardClick={searchCardClick}
            cardListType={CardListType.Fabric}
            inputTestId="fabric-search-input"
            showScanner={state.client === 'inStore'}
            triggerScanner={scanTrigger}
            setTriggerScan={setTriggerScan}
            clickedFabricGroup={clickedFabricGroup}
          />
        )}
      </Panel>

      {/* FabricDetails Panel */}
      <Panel
        isActive={isFabricInfoShown}
        closeClick={() => {
          setIsFabricInfoShown(false);
        }}
        panelSize={PanelSize.Content}
      >
        {isFabricInfoShown && state.selectedFabric && state.product && (
          <FabricInfo
            showBookletInfo={state.client === Clients.inStore}
            closeFabricInfo={() => {
              setIsFabricInfoShown(false);
            }}
          />
        )}
      </Panel>

      {/* Filter Panel */}
      <Panel
        isActive={isFilterShown}
        closeClick={() => {
          setIsFilterShown(false);
        }}
        panelSize={PanelSize.Full}
      >
        {isFilterShown && (
          <FilterList
            filters={fabricFilters}
            onClose={() => {
              setIsFilterShown(false);
            }}
            onFilterChange={countFilter}
            onFilterApply={applyFilter}
            selectedSortBy={selectedSortBy}
            selectedFilter={selectedFilter}
            setSelectedSortBy={setSelectedSortBy}
            setSelectedFilter={setSelectedFilter}
          />
        )}
      </Panel>
    </>
  );
};
